import { Anchor, List, Text } from "@mantine/core"

import {
  BulletedListContentSchema,
  ComponentContent,
  ContentBlock,
  FormattedTextContent,
} from "@tfg/types/schemas/communication-studio/components"
import {
  textColorCssVariable,
  textFontSizeCSSVariable,
  textFontWeightCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"

import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"

import { useGetContentInLanguage } from "./TextParagraph"

type BulletedListProps = {
  componentContent: ComponentContent
}

export const BulletedList = ({ componentContent }: BulletedListProps) => {
  const contentBlocks = useGetContentInLanguage(componentContent)
  return renderBulletedList(contentBlocks?.[0] ?? undefined)
}

export function renderFormattedText(item: FormattedTextContent) {
  const { text, formatting = [] } = item

  const sortedFormatting = formatting.sort((a, b) => a.start - b.start)

  const result = []
  let lastIndex = 0

  sortedFormatting.forEach((format) => {
    if (format.start > lastIndex) {
      result.push(text.slice(lastIndex, format.start))
    }

    const formattedText = text.slice(format.start, format.end)
    switch (format.type) {
      case "strong":
        result.push(
          <Text fw="bold" component="strong" key={format.start}>
            {formattedText}
          </Text>
        )
        break
      case "hyperlink":
        result.push(
          <Anchor key={format.start} href={format.link}>
            {formattedText}
          </Anchor>
        )
        break
      // ... other types
    }

    lastIndex = format.end
  })
  // Add remaining unformatted text
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex))
  }

  return result
}

export function renderBulletedList(
  listContent: ContentBlock | undefined,
  index = 0
) {
  if (!listContent) {
    return null
  }
  const textContent = BulletedListContentSchema.safeParse(
    listContent.textContent
  )
  if (!textContent.success) {
    console.error(textContent.error)
    return null
  }
  const { items, style = "disc" } = textContent.data

  return (
    <List
      type={style === "disc" ? "unordered" : "ordered"}
      c={DesignStudioParametersToolkit.getCssVariableLiteralName(
        textColorCssVariable
      )}
      key={`${listContent.type}-${index}`}
    >
      {items.map((item, index) => (
        <List.Item key={`list-item-${index}`}>
          <Text
            fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontSizeCSSVariable
            )}
            fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textFontWeightCSSVariable
            )}
            c={DesignStudioParametersToolkit.getCssVariableLiteralName(
              textColorCssVariable
            )}
          >
            {renderFormattedText(item)}
          </Text>
        </List.Item>
      ))}
    </List>
  )
}
